import React, { useState, useEffect } from "react";
import { useAuth } from "../contexts/AuthContext";
import { useNavigation } from "../contexts/NavigationContext";
import {
  GoogleAddressAutocomplete,
  AddressDetails,
} from "./common/GoogleAddressAutocomplete";
import { Address } from "../types";
import { Loader, MapPin, User, Briefcase, Users } from "lucide-react";
import { APP_NAME } from "../constants/app";

export type AccountType = "client" | "contractor" | "both";

export const Onboarding: React.FC = () => {
  const { refreshUser } = useAuth();
  const { navigate } = useNavigation();
  const [step, setStep] = useState<1 | 2>(1);
  const [accountType, setAccountType] = useState<AccountType | null>(null);
  const [companyName, setCompanyName] = useState("");
  const [address, setAddress] = useState<Address>({});
  const [addressFormatted, setAddressFormatted] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const apiUrl = process.env.REACT_APP_API_URL || "http://localhost:5000";

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token) {
      navigate("/auth/login");
      return;
    }
  }, [navigate]);

  const handleAddressSelect = (details: AddressDetails) => {
    setAddress({
      formatted: details.formatted,
      streetNumber: details.streetNumber,
      streetName: details.streetName,
      street: details.street,
      city: details.city,
      state: details.state,
      postalCode: details.postalCode,
      country: details.country,
      lat: details.lat,
      lng: details.lng,
    });
    setAddressFormatted(details.formatted);
  };

  const needsAddress = accountType === "contractor" || accountType === "both";

  const handleStep2Submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsLoading(true);

    try {
      const token = localStorage.getItem("token");
      if (!token) {
        throw new Error("Authentication required");
      }

      if (!companyName.trim()) {
        throw new Error("Company name is required");
      }

      if (needsAddress && !addressFormatted.trim()) {
        throw new Error("Company address is required for contractors");
      }

      const payload: {
        token: string;
        companyName: string;
        accountType: AccountType;
        address?: Address;
      } = {
        token,
        companyName: companyName.trim(),
        accountType: accountType!,
      };

      if (needsAddress && addressFormatted.trim()) {
        payload.address = (address.formatted ? address : { formatted: addressFormatted }) as Address;
      }

      const response = await fetch(`${apiUrl}/api/sso/create-account`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });

      const data = await response.json();

      if (!data.success) {
        throw new Error(data.error || "Failed to create account");
      }

      localStorage.setItem("token", data.token);
      localStorage.setItem("user", JSON.stringify(data.user));

      await refreshUser();
      navigate("/");
    } catch (err: any) {
      console.error("Create account error:", err);
      setError(err.message || "Failed to create account. Please try again.");
      setIsLoading(false);
    }
  };

  // Step 1: Role selection
  if (step === 1) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900 py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-2xl w-full">
          <div className="text-center mb-10">
            <div className="mb-4">
              <img src="/logo.svg" alt={APP_NAME} className="w-20 h-20 mx-auto object-contain" />
            </div>
            <h2 className="mt-6 text-3xl font-extrabold text-gray-900 dark:text-gray-100">
              Welcome to {APP_NAME}
            </h2>
            <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
              How will you use the platform?
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <button
              type="button"
              onClick={() => {
                setAccountType("client");
                setStep(2);
              }}
              className="relative flex flex-col items-center p-6 rounded-xl border-2 border-gray-200 dark:border-gray-700 hover:border-accent-500 dark:hover:border-accent-500 hover:bg-accent-50/30 dark:hover:bg-accent-900/20 transition-all group"
            >
              <div className="rounded-full p-4 bg-blue-100 dark:bg-blue-900/40 group-hover:bg-accent-100 dark:group-hover:bg-accent-900/40 transition-colors">
                <User className="h-8 w-8 text-blue-600 dark:text-blue-400" />
              </div>
              <h3 className="mt-3 text-lg font-semibold text-gray-900 dark:text-gray-100">Client</h3>
              <p className="mt-1 text-sm text-gray-500 dark:text-gray-400 text-center">
                I hire contractors and manage projects. Minimal setup.
              </p>
            </button>

            <button
              type="button"
              onClick={() => {
                setAccountType("contractor");
                setStep(2);
              }}
              className="relative flex flex-col items-center p-6 rounded-xl border-2 border-gray-200 dark:border-gray-700 hover:border-accent-500 dark:hover:border-accent-500 hover:bg-accent-50/30 dark:hover:bg-accent-900/20 transition-all group"
            >
              <div className="rounded-full p-4 bg-amber-100 dark:bg-amber-900/40 group-hover:bg-accent-100 dark:group-hover:bg-accent-900/40 transition-colors">
                <Briefcase className="h-8 w-8 text-amber-600 dark:text-amber-400" />
              </div>
              <h3 className="mt-3 text-lg font-semibold text-gray-900 dark:text-gray-100">Contractor</h3>
              <p className="mt-1 text-sm text-gray-500 dark:text-gray-400 text-center">
                I run projects and work with clients. Full features & integrations.
              </p>
            </button>

            <button
              type="button"
              onClick={() => {
                setAccountType("both");
                setStep(2);
              }}
              className="relative flex flex-col items-center p-6 rounded-xl border-2 border-gray-200 dark:border-gray-700 hover:border-accent-500 dark:hover:border-accent-500 hover:bg-accent-50/30 dark:hover:bg-accent-900/20 transition-all group"
            >
              <div className="rounded-full p-4 bg-green-100 dark:bg-green-900/40 group-hover:bg-accent-100 dark:group-hover:bg-accent-900/40 transition-colors">
                <Users className="h-8 w-8 text-green-600 dark:text-green-400" />
              </div>
              <h3 className="mt-3 text-lg font-semibold text-gray-900 dark:text-gray-100">Both</h3>
              <p className="mt-1 text-sm text-gray-500 dark:text-gray-400 text-center">
                I'm both a client and contractor. Full setup.
              </p>
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Step 2: Details form
  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div className="text-center">
          <button
            type="button"
            onClick={() => setStep(1)}
            className="text-sm text-gray-500 hover:text-gray-700 dark:hover:text-gray-400 mb-4"
          >
            ← Back
          </button>
          <div className="mb-4">
            <img src="/logo.svg" alt={APP_NAME} className="w-20 h-20 mx-auto object-contain" />
          </div>
          <h2 className="mt-6 text-3xl font-extrabold text-gray-900 dark:text-gray-100">
            {accountType === "client" ? "Almost done" : "Complete your account"}
          </h2>
          <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
            {accountType === "client"
              ? "Just your company or project name – you can add more later."
              : "We need a few details to set up your account."}
          </p>
        </div>

        <form className="mt-8 space-y-6" onSubmit={handleStep2Submit}>
          {error && (
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-300 px-4 py-3 rounded">
              {error}
            </div>
          )}

          <div className="space-y-4">
            <div>
              <label
                htmlFor="companyName"
                className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
              >
                {accountType === "client" ? "Company or project name" : "Company name"}{" "}
                <span className="text-red-500">*</span>
              </label>
              <input
                id="companyName"
                type="text"
                required
                value={companyName}
                onChange={(e) => setCompanyName(e.target.value)}
                className="appearance-none relative block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 placeholder-gray-500 text-gray-900 dark:text-gray-100 bg-white dark:bg-gray-800 rounded-md focus:outline-none focus:ring-accent-500 focus:border-accent-500 sm:text-sm"
                placeholder={
                  accountType === "client"
                    ? "e.g. Acme Corp or My Home Renovation"
                    : "Enter your company name"
                }
              />
            </div>

            {needsAddress && (
              <div>
                <label
                  htmlFor="address"
                  className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
                >
                  Company address <span className="text-red-500">*</span>
                </label>
                <GoogleAddressAutocomplete
                  id="address"
                  value={addressFormatted}
                  onChange={setAddressFormatted}
                  onPlaceSelect={handleAddressSelect}
                  placeholder="Enter your company address"
                  className="appearance-none relative block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 placeholder-gray-500 text-gray-900 dark:text-gray-100 bg-white dark:bg-gray-800 rounded-md focus:outline-none focus:ring-accent-500 focus:border-accent-500 sm:text-sm"
                  required={needsAddress}
                />
                {addressFormatted && (
                  <div className="mt-2 flex items-start text-sm text-gray-600 dark:text-gray-400">
                    <MapPin className="h-4 w-4 mr-1 mt-0.5 flex-shrink-0" />
                    <span>{addressFormatted}</span>
                  </div>
                )}
              </div>
            )}

            {accountType === "client" && (
              <p className="text-xs text-gray-500 dark:text-gray-400">
                As a client, you can connect integrations and add more details later from Settings.
              </p>
            )}
          </div>

          <div>
            <button
              type="submit"
              disabled={
                isLoading ||
                !companyName.trim() ||
                (needsAddress && !addressFormatted.trim())
              }
              className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-accent-600 hover:bg-accent-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-accent-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoading ? (
                <>
                  <Loader className="animate-spin -ml-1 mr-3 h-5 w-5" />
                  Creating account...
                </>
              ) : (
                "Create account"
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
